import PropTypes from 'prop-types';
import { Box, Button, Grid2, Stack } from '@mui/material';

import { componentsSize } from 'constants/components';
import { CHART_TABLE_CELL_VARIANTS } from 'constants/dashboard';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import ChartTable from 'components/complex/ChartTable';
import { DashboardBox, DashboardTabs, Info, NoData } from 'components/simple';
import { PieChart, Radar, RadialChart } from 'components/charts';

const styles = {
  dashboardBox: ({ breakpoints }, withTooltip = false) => ({
    ...(withTooltip && { overflow: 'visible' }),
    height: '100%',
    minHeight: 330,
    [breakpoints.up('sm')]: {
      minHeight: 400,
    },
    [breakpoints.up('md')]: {
      minHeight: 480,
    },
  }),
  graphInfo: ({ breakpoints }) => ({
    position: 'relative',
    top: -16,
    width: '100%',
    maxWidth: 275,
    height: 60,
    gap: 2,
    [breakpoints.up('md')]: {
      top: -24,
    },
  }),
  boxContent: ({ breakpoints }) => ({
    height: 280,
    [breakpoints.up('md')]: {
      height: 400,
    },
  }),
  bucketContent: ({ breakpoints }, bucketData) => ({
    height: bucketData?.growing !== undefined ? 440 : 390,
    [breakpoints.up('md')]: {
      height: bucketData?.growing !== undefined ? 580 : 520,
    },
  }),
  communityContent: (theme, firstBucket) => ({
    height: 280,
    [theme.breakpoints.up('md')]: {
      ...(firstBucket && styles.bucketContent(theme, firstBucket)),
    },
    [theme.breakpoints.up('xl')]: {
      height: 400,
    },
  }),
  bucketTableContainer: ({ breakpoints }, hasGrowing) => ({
    height: hasGrowing ? 277 : 227,
    [breakpoints.up('md')]: {
      height: hasGrowing ? 397 : 337,
    },
  }),
  radar: ({ breakpoints }) => ({
    width: '100%',
    height: 235,
    [breakpoints.up('md')]: {
      height: 360,
    },
  }),
};

export const DashboardStats = ({
  cultural,
  sample,
  sampleNext = null,
  samplePrev = null,
  community,
  buckets,
  goToKeyAreaDetails,
}) => {
  const { isMdDown: isMobile, getResponsiveValueForCurrent } = useBreakpointFlags();
  const isGrowingData = !!cultural.growing || cultural.growing === 0;
  const isParticipationData = !!cultural.participation || cultural.participation === 0;
  const isAnswersCountData = !!cultural.answersNumber || cultural.answersNumber === 0;
  const sampleSubtitleLength = getResponsiveValueForCurrent({
    xs: 30,
    sm: 25,
    md: 30,
    lg: 45,
    xl: 20,
    xxl: 30,
  });

  return (
    <Grid2 container mt={8} columnSpacing={{ xs: 0, sm: 1, md: 4 }} rowSpacing={4} mb={{ xs: 5, xl: 8 }}>
      <Grid2 size={{ xs: 12, md: 6, xxl: 4 }}>
        <DashboardBox title="Cultural Performance" customStyles={styles.dashboardBox}>
          {cultural.performance ? (
            <Stack width="100%" spacing={2} justifyContent="space-around" alignItems="center">
              <RadialChart
                value={cultural.performance}
                chartContainerHeight={isMobile ? 120 : 170}
                innerRadius={isMobile ? 80 : 120}
                outerRadius={isMobile ? 140 : 200}
                barSize={isMobile ? 60 : 80}
              />

              <Stack direction="row" gap={2} width="100%" height={{ xs: 70, md: 100 }}>
                {isGrowingData && (
                  <Info
                    isComparison
                    title={`${cultural.growing}%`}
                    withPlusSign={cultural.growing > 0}
                    variant={cultural.growing_color}
                  />
                )}
                {isParticipationData && (
                  <Info title={`${cultural.participation}%`} description="Participation" variant="normal" />
                )}
                {isAnswersCountData && (
                  <Info
                    isComparison={isGrowingData}
                    isRowDirection={!isGrowingData}
                    title={isGrowingData ? cultural.answersNumber.toString() : `${cultural.answersNumber} surveys`}
                    titleDescription={isGrowingData ? 'surveys' : ''}
                    withPlusSign={isGrowingData && cultural.answersNumber > 0}
                    variant="normal"
                  />
                )}
              </Stack>
            </Stack>
          ) : (
            <NoData />
          )}
        </DashboardBox>
      </Grid2>

      <Grid2 size={{ xs: 12, md: 6, xxl: 4 }}>
        <DashboardBox
          withSubtitleTooltip
          title="Sample Size"
          subTitle={sample?.groupName}
          next={sampleNext}
          prev={samplePrev}
          customStyles={(muiTheme) => styles.dashboardBox(muiTheme, true)}
          customContentStyles={styles.boxContent}
          subTitleLength={sampleSubtitleLength}
        >
          {sample?.graph ? (
            <DashboardTabs defaultActiveTabTitle="Graph">
              <Box title="Graph" mt={{ xs: 0.5, sm: 2, md: 3 }}>
                <PieChart data={sample?.graph} size={isMobile ? componentsSize.small : componentsSize.medium} />
              </Box>

              <ChartTable title="Segments" data={sample?.table} />
            </DashboardTabs>
          ) : (
            <NoData />
          )}
        </DashboardBox>
      </Grid2>

      <Grid2 size={{ xs: 12, md: 6, xxl: 4 }}>
        <DashboardBox
          title="Community Insights"
          customStyles={styles.dashboardBox}
          customContentStyles={(muiTheme) => styles.communityContent(muiTheme, buckets[0])}
        >
          {community.strength || community.challenge ? (
            <DashboardTabs
              defaultActiveTabTitle={!community.strength && community.challenge ? 'Challenges' : 'Strengths'}
            >
              <ChartTable title="Strengths" data={community.strength} noData={!community.strength} />

              <ChartTable title="Challenges" data={community.challenge} noData={!community.challenge} />
            </DashboardTabs>
          ) : (
            <NoData />
          )}
        </DashboardBox>
      </Grid2>

      {buckets.map((item) => (
        <Grid2 key={item.id} size={{ xs: 12, md: 6, xxl: buckets.length % 2 ? 4 : 6 }}>
          <DashboardBox title={item.name} customContentStyles={(muiTheme) => styles.bucketContent(muiTheme, item)}>
            <Stack width="100%" height="100%" justifyContent="space-between">
              <DashboardTabs defaultActiveTabTitle="Graph">
                <Stack alignItems="center" justifyContent="space-around" width="100%" height="100%" title="Graph">
                  <Box sx={styles.radar}>
                    <Radar data={item.graph} />
                  </Box>

                  {(item.growing || item.growing === 0) && (
                    <Info
                      isComparison
                      isRowDirection
                      title={`${item?.growing > 0 ? `+${item?.growing}` : item?.growing}%`}
                      variant={item?.growing_color}
                      customStyles={styles.graphInfo}
                    />
                  )}
                </Stack>

                <ChartTable
                  title="Pillars"
                  data={item.table}
                  containerSx={(theme) => styles.bucketTableContainer(theme, item?.growing !== undefined)}
                />
              </DashboardTabs>

              <Button onClick={() => goToKeyAreaDetails(item)}>Explore Key Area</Button>
            </Stack>
          </DashboardBox>
        </Grid2>
      ))}
    </Grid2>
  );
};

const nullableNumberType = PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]);
const chartTableCellType = {
  variant: PropTypes.oneOf(Object.values(CHART_TABLE_CELL_VARIANTS)),
  color: PropTypes.string,
  text: PropTypes.string,
  tooltip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
const chartTableColumnType = {
  id: PropTypes.string,
  cells: PropTypes.arrayOf(PropTypes.shape(chartTableCellType)),
};
const bucketGraphItemType = {
  name: PropTypes.string,
  value: PropTypes.number,
  color: PropTypes.string,
};

DashboardStats.propTypes = {
  cultural: PropTypes.shape({
    performance: PropTypes.number,
    growing_color: PropTypes.string,
    growing: nullableNumberType,
    participation: nullableNumberType,
    answersNumber: nullableNumberType,
  }).isRequired,
  sample: PropTypes.shape({
    graph: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.number,
        color: PropTypes.string,
      }),
    ),
    table: PropTypes.shape({
      header: PropTypes.arrayOf(PropTypes.string),
      columns: PropTypes.arrayOf(PropTypes.shape(chartTableColumnType)),
    }),
  }),
  sampleNext: PropTypes.func,
  samplePrev: PropTypes.func,
  community: PropTypes.shape({
    challenge: PropTypes.shape({
      header: PropTypes.arrayOf(PropTypes.string),
      columns: PropTypes.arrayOf(PropTypes.shape(chartTableColumnType)),
    }),
    strength: PropTypes.shape({
      header: PropTypes.arrayOf(PropTypes.string),
      columns: PropTypes.arrayOf(PropTypes.shape(chartTableColumnType)),
    }),
  }),
  buckets: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.string,
      growing: PropTypes.number,
      growing_color: PropTypes.string,
      table: PropTypes.shape({
        header: PropTypes.arrayOf(PropTypes.string),
        columns: PropTypes.arrayOf(PropTypes.shape(chartTableColumnType)),
      }),
      graph: PropTypes.arrayOf(PropTypes.shape(bucketGraphItemType)),
    }),
  ).isRequired,
  goToKeyAreaDetails: PropTypes.func.isRequired,
};
